<template>
  <div class="ai-assistant">
    <div class="page-header">
      <div class="page-icon">🤖</div>
      <h2>AI 经营助手</h2>
    </div>

    <el-card class="chat-card">
      <div class="chat-messages" ref="messagesRef">
        <div v-if="messages.length === 0" class="welcome">
          <div class="welcome-icon">💡</div>
          <div class="welcome-text">你好！我是你的经营分析助手。</div>
          <div class="welcome-sub">基于近 30 天的经营数据，我可以帮你：</div>
          <div class="suggestions">
            <el-tag
              v-for="q in suggestions"
              :key="q"
              class="suggestion-tag"
              @click="askSuggestion(q)"
            >
              {{ q }}
            </el-tag>
          </div>
        </div>

        <div
          v-for="(msg, idx) in messages"
          :key="idx"
          class="message"
          :class="msg.role"
        >
          <div class="msg-avatar">{{ msg.role === 'user' ? '🧑' : '🤖' }}</div>
          <div class="msg-bubble">
            <div v-if="msg.role === 'assistant' && msg.loading" class="loading-dots">
              <span></span><span></span><span></span>
            </div>
            <div v-else class="msg-content" v-html="formatAnswer(msg.content)"></div>
          </div>
        </div>
      </div>

      <div class="chat-input">
        <el-input
          v-model="inputText"
          type="textarea"
          :rows="2"
          placeholder="输入你的问题，例如：最近利润怎么样？"
          resize="none"
          @keydown.enter.exact.prevent="sendMessage"
        />
        <el-button
          type="primary"
          :disabled="!inputText.trim() || loading"
          @click="sendMessage"
        >
          发送
        </el-button>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted } from 'vue';
import axios from 'axios';
import API_BASE from '@/api';
import { ElMessage } from 'element-plus';

const messages = ref([]);
const inputText = ref('');
const loading = ref(false);
const messagesRef = ref(null);

const suggestions = [
  '最近 30 天经营情况怎么样？',
  '哪些商品最赚钱？',
  '哪些商品库存不足需要补货？',
  '利润有什么变化趋势？'
];

const aiEnabled = ref(true);

onMounted(async () => {
  try {
    const res = await axios.get(`${API_BASE}/api/ai/status`, { timeout: 10000 });
    aiEnabled.value = !!res.data.enabled;
    if (!aiEnabled.value) {
      ElMessage.warning('AI 功能未启用：后端未配置 DEEPSEEK_API_KEY');
    }
  } catch (e) {
    // 状态接口失败不再禁用提问，让真正的原因在聊天里显示出来
    console.warn('[AI] 状态检查失败：', e && e.message);
  }
});

function scrollToBottom() {
  nextTick(() => {
    if (messagesRef.value) {
      messagesRef.value.scrollTop = messagesRef.value.scrollHeight;
    }
  });
}

function escapeHtml(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function formatAnswer(content) {
  // 用 String(content ?? '') 兜底：内容是 null/undefined 时也不能让渲染函数抛错，
  // 否则整条消息会一直停在“加载中”。
  const text = String(content == null ? '' : content);
  if (!text) return '';
  // 先转义 HTML，再渲染换行和 **加粗**，避免 v-html 注入与解析异常
  const safe = escapeHtml(text);
  return safe
    .replace(/\r\n/g, '\n')
    .replace(/\n/g, '<br/>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
}

/**
 * 把后端返回体归一化成一段可显示的文本。
 * 兼容三种情况：正常 JSON、answer 被二次 JSON 序列化、非 JSON 纯文本。
 */
function normalizeAnswer(data) {
  if (typeof data === 'string') {
    const raw = data.trim();
    if (raw.startsWith('{') || raw.startsWith('[')) {
      try {
        return normalizeAnswer(JSON.parse(raw));
      } catch (e) {
        return raw;
      }
    }
    return raw;
  }
  if (!data || typeof data !== 'object') return '';

  const text = data.answer || data.data?.answer || data.content || data.message || '';
  if (text && typeof text === 'object') return JSON.stringify(text);

  // answer 本身可能是被序列化过的 JSON 字符串，继续拆一层
  const raw = String(text || '').trim();
  if (raw.startsWith('{') || raw.startsWith('[')) {
    try {
      return normalizeAnswer(JSON.parse(raw));
    } catch (e) {
      return raw;
    }
  }
  return raw;
}

function askSuggestion(q) {
  if (loading.value) return;
  inputText.value = q;
  sendMessage();
}

async function sendMessage() {
  const question = inputText.value.trim();
  if (!question || loading.value) return;

  messages.value.push({ role: 'user', content: question });
  inputText.value = '';
  scrollToBottom();

  messages.value.push({ role: 'assistant', content: '', loading: true });
  // 记住这条「加载中」气泡的下标，之后必须通过 messages.value[idx] 去改，
  // 直接改 push 前的原始对象不会触发 Vue 重新渲染（这是「一直转圈」的根因）。
  const idx = messages.value.length - 1;
  loading.value = true;
  scrollToBottom();

  let answer = '';
  try {
    const res = await axios.post(
      `${API_BASE}/api/ai/chat`,
      { question },
      { timeout: 90000, headers: { 'Content-Type': 'application/json' } }
    );

    const data = res && res.data;
    if (data && data.success) {
      answer = normalizeAnswer(data);
      if (!answer) answer = 'AI 没有返回内容（answer 为空），请稍后重试。';
    } else if (data) {
      answer = '抱歉，分析失败：' + (data.message || '未知错误');
    } else {
      answer = '后端返回了空响应，请确认线上后端已更新到最新版本（包含 /api/ai/chat 路由）。';
    }
  } catch (e) {
    const msg =
      (e.response && e.response.data && e.response.data.message) ||
      (e.response ? `HTTP ${e.response.status}` : '') ||
      e.message ||
      '网络错误';
    answer = '请求出错：' + msg;
    console.error('[AI] 请求失败：', e);
  } finally {
    if (messages.value[idx]) {
      messages.value[idx].content = answer;
      messages.value[idx].loading = false;
    }
    loading.value = false;
    scrollToBottom();
  }
}
</script>

<style scoped>
.ai-assistant {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}
.page-icon {
  font-size: 24px;
}

.chat-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow: hidden;
}
.chat-card :deep(.el-card__body) {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 8px 4px;
}

.welcome {
  text-align: center;
  padding: 40px 20px;
}
.welcome-icon {
  font-size: 48px;
  margin-bottom: 16px;
}
.welcome-text {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
}
.welcome-sub {
  color: var(--text-tertiary, #909399);
  font-size: 14px;
  margin-bottom: 20px;
}
.suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
}
.suggestion-tag {
  cursor: pointer;
  transition: all 0.2s;
}
.suggestion-tag:hover {
  background-color: var(--primary, #5b8def);
  color: #fff;
  border-color: var(--primary, #5b8def);
}

.message {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
}
.message.user {
  flex-direction: row-reverse;
}
.msg-avatar {
  font-size: 24px;
  flex-shrink: 0;
}
.msg-bubble {
  max-width: 70%;
  padding: 12px 16px;
  border-radius: 12px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}
.message.user .msg-bubble {
  background-color: var(--primary, #5b8def);
  color: #fff;
  border-top-right-radius: 4px;
}
.message.assistant .msg-bubble {
  background-color: var(--bg-card, #f5f7fa);
  color: var(--text-primary, #303133);
  border-top-left-radius: 4px;
}
.msg-content {
  font-size: 14px;
}
.msg-content :deep(strong) {
  font-weight: 700;
}

.loading-dots {
  display: flex;
  gap: 4px;
  padding: 4px 0;
}
.loading-dots span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: var(--text-tertiary, #c0c4cc);
  animation: bounce 1.4s infinite ease-in-out both;
}
.loading-dots span:nth-child(1) { animation-delay: -0.32s; }
.loading-dots span:nth-child(2) { animation-delay: -0.16s; }

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
}

.chat-input {
  display: flex;
  gap: 10px;
  padding-top: 12px;
  border-top: 1px solid var(--border-lighter, #ebeef5);
}
.chat-input :deep(.el-textarea) {
  flex: 1;
}
</style>
